import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Card, Button } from 'react-bootstrap';
import { VerticalSliderOverlay } from './ChannelColumn';

function AuxSendColumn({ inputChannel, auxIndex, getEntryValue, setEntryValue }) {
  const [openMenu, setOpenMenu] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const nameDownRef = useRef(null);

  const readSend = (key) => {
    return getEntryValue ? getEntryValue('i', inputChannel.index, `aux.${auxIndex}.${key}`) : '';
  };

  const parseSendFloat = (key, defaultValue) => {
    const num = parseFloat(readSend(key));
    return isNaN(num) ? defaultValue : num;
  };

  const parseSendBool = (key) => {
    const raw = readSend(key);
    return String(raw) === '1' || raw === 1;
  };

  const writeSend = useCallback((key, val) => {
    if (setEntryValue) setEntryValue('i', inputChannel.index, `aux.${auxIndex}.${key}`, val);
  }, [setEntryValue, inputChannel.index, auxIndex]);

  const handleValueChange = useCallback((v) => { writeSend('value', v); }, [writeSend]);

  const muteOn = parseSendBool('mute');
  const isPost = parseSendBool('post');
  const postProcOn = parseSendBool('postproc');

  return (
    <div className="channel-column">
      {/* Pan slider */}
      <div className="panner-wrapper">
        <input
          type="range"
          min="0"
          max="1"
          step="0.001"
          className="panner-slider"
          value={parseSendFloat('pan', 0.5)}
          onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) writeSend('pan', v); }}
          onDoubleClick={() => { writeSend('pan', 0.5); }}
        />
      </div>

      {/* Buttons */}
      <div className="button-rows">
        {/* Mute button */}
        <div className="button-row single">
          <Button variant={muteOn ? 'primary' : 'secondary'} size="sm" onClick={() => writeSend('mute', muteOn ? 0 : 1)}>
            MUTE
          </Button>
        </div>

        {/* PRE/POST toggle */}
        <div className="button-row single">
          <Button variant={isPost ? 'primary' : 'secondary'} size="sm" onClick={() => writeSend('post', isPost ? 0 : 1)}>
            {isPost ? 'POST' : 'PRE'}
          </Button>
        </div>

        {/* Post proc toggle */}
        <div className="button-row single">
          <Button variant={postProcOn ? 'primary' : 'secondary'} size="sm" onClick={() => writeSend('postproc', postProcOn ? 0 : 1)}>
            POST PROC
          </Button>
        </div>
      </div>

      {/* Value slider (vertical) */}
      <div className="slider-area">
        <div className="control-col">
          <div className="slider-wrapper">
            <VerticalSliderOverlay
              value={parseSendFloat('value', 0)}
              onChange={handleValueChange}
              onDoubleClick={() => writeSend('value', 1)}
              maxLinear={10}
            />
          </div>
          <input
            className="gain-input"
            type="number"
            step={0.1}
            min={-120}
            max={12}
            value={(() => { const num = parseSendFloat('value', 0); if (num <= 0) return (-120).toFixed(1); const db = 20 * Math.log10(num); return (Math.round(db * 10) / 10).toFixed(1); })()}
            onChange={(e) => { const db = parseFloat(String(e.target.value).replace(',', '.')); if (isNaN(db) || !setEntryValue) return; const linear = Math.pow(10, db / 20); writeSend('value', linear); }}
          />
        </div>
      </div>

      {/* Channel name (clickable for rename only – no group selection) */}
      <div>
        <div
          className="channel-name-btn no-drag"
          role="button"
          tabIndex={0}
          onPointerDown={(e) => { nameDownRef.current = { x: e.clientX, y: e.clientY }; }}
          onClick={(e) => {
            const down = nameDownRef.current;
            nameDownRef.current = null;
            if (down) {
              const dx = Math.abs(e.clientX - down.x);
              const dy = Math.abs(e.clientY - down.y);
              if (dx + dy > 6) return;
            }
            const r = e.target && e.target.getBoundingClientRect && e.target.getBoundingClientRect();
            if (r) {
              const menuW = 180;
              const centerX = r.left + r.width / 2;
              const left = Math.min(Math.max(centerX - menuW / 2, 8), window.innerWidth - menuW - 8);
              setMenuPos({ left, top: r.top });
            }
            setOpenMenu((v) => !v);
          }}
        >
          {inputChannel.name || ''}
        </div>
      </div>

      {/* Rename popup (no group selection) */}
      {openMenu && menuPos && ReactDOM.createPortal(
        <div className="channel-menu" style={{ left: menuPos.left, top: menuPos.top, transform: 'translateY(-100%)', width: '180px' }}>
          <div className="menu-actions">
            <div className="rename-row">
              <button onClick={() => {
                const newName = window.prompt('Rename channel', inputChannel.name || '');
                if (newName !== null && setEntryValue) {
                  setEntryValue('i', inputChannel.index, 'name', newName);
                }
                setOpenMenu(false);
              }}>RENAME</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function AuxSendList({ auxChannel, inputChannels, getEntryValue, setEntryValue }) {
  if (!inputChannels || inputChannels.length === 0) {
    return <div className="p-3">No input channels found</div>;
  }

  return (
    <Card className="mb-3 h-100">
      <Card.Body className="d-flex flex-column h-100">
        <div className="channels-area">
          <div className="channels-scroll">
            <div className="channels-row-inner">
              {inputChannels.map((inputCh) => (
                <AuxSendColumn
                  key={inputCh.index}
                  inputChannel={inputCh}
                  auxIndex={auxChannel.index}
                  getEntryValue={getEntryValue}
                  setEntryValue={setEntryValue}
                />
              ))}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

